// Pure predicate for "does this issue need a SEARCH for an existing
// household to merge into?" — the gap Grant hit on 2026-07-08 (Silas
// Norton should join his dad's household; Matthew Raleigh's quarantined
// joint account should attach to the other Matthew Raleigh's household).
// spouse-pass / trust-reattach's review branch recommend a merge but only
// know ONE household (the source) — the destination has to be found by
// searching, unlike household-cluster-predicate's 2-known-households
// case. Split out (same convention as household-cluster-predicate.ts) so
// it's unit-testable from tests/web/ without dragging web/src/api.ts
// (DOM-typed) into the root tsconfig program.
//
// Deliberately an explicit slug allowlist, not a broad shape heuristic —
// a detector's affectedRecords/verb shape matching by accident doesn't
// mean "search and merge into an existing household" is the right
// resolution for it. Add a slug here only after confirming its
// recommendation copy actually describes this flow.

export type RecordRef = { kind: string; id: string; label: string };

// Minimal subset of CleanupIssueSummary this predicate inspects.
export interface IssueLike {
  entityKind: string;
  detectorSlug?: string;
  affectedRecords?: RecordRef[];
  recommendation?: { verb: string; label?: string } | null;
}

// trust-reattach's non-auto-resolvable "review" branch covers three
// statuses with the same verb/shape (single quarantined-household ref):
// trust_no_family, needs_review (both: "Open to handle manually" — find
// or create the right family and merge manually — a direct fit for this
// flow), and multi_account_collapsed ("Open to split first" — genuinely
// needs the split flow FIRST, not a straight search-merge). Excluded by
// label since the predicate only sees the issue shape, not the raw
// detector status.
const TRUST_REATTACH_SPLIT_FIRST_LABEL = "Open to split first";

// Grant 2026-09-10 — the two import-stub REVIEW detectors shipped on
// 2026-09-09 (mortgage-import-stub-review, financial-import-stub-review).
// Both ask "which real family does this belong to?" about a single
// placeholder household, and both carry NO recommendation on purpose: the
// data genuinely does not settle it, and a one-click merge on an
// unanswered question is how the 2026-07-16 mis-merges happened.
//
// But "no recommendation" is not "no answer possible" — it means the
// PERSON has the answer and the machine does not. Without an entry here
// they fell through to the rail's generic disposition buttons (not an
// issue / snooze / assign), so there was nowhere to record the one thing
// the steward had worked out. That is the exact dead end Cambria hit on
// 2026-07-31 — "ok so i figured out which household is the right one but
// im not seeing a spot where i can fix it" — and 90 items across Joey's
// and Brian Ream's queues shipped in that state.
//
// The rail's button already falls back to "Find & merge into an existing
// household…" when there is no recommendation label, so nothing else
// needed changing. These two slugs are the ONLY ones exempt from the verb
// test below: a missing recommendation on any other detector still means
// no search-merge button.
const NO_RECOMMENDATION_BY_DESIGN = new Set([
  "mortgage-import-stub-review",
  "financial-import-stub-review",
]);

export function householdSearchMergeSource(
  issue: IssueLike,
): RecordRef | null {
  if (issue.entityKind !== "household") return null;
  const slug = issue.detectorSlug;
  if (slug != null && NO_RECOMMENDATION_BY_DESIGN.has(slug)) {
    // One placeholder household, and the steward names the family it
    // belongs to. A second household ref would mean the destination is
    // already known, which is the cluster overlay's shape, not this one.
    const recs = (issue.affectedRecords ?? []).filter(
      (r) => r.kind === "household",
    );
    return recs.length === 1 ? recs[0]! : null;
  }
  const verb = issue.recommendation?.verb;
  if (verb !== "merge" && verb !== "review") return null;
  if (
    slug === "spouse-pass" ||
    slug === "hh-one-contact" ||
    // Baker 2026-07-13 — hh-quarantined ("Is X a real, distinct
    // household?"): its merge answer opens this same search-for-the-
    // destination flow. Single household ref, verb "review".
    slug === "hh-quarantined"
  ) {
    // [household, contact] — the household ref is the source. Exactly ONE
    // household ref required: spouse-pass's phone-matched shape
    // (2026-07-30) carries TWO household refs and resolves via the
    // per-field cluster overlay (household-cluster-predicate), not by
    // searching for a destination it already names.
    const recs = (issue.affectedRecords ?? []).filter(
      (r) => r.kind === "household",
    );
    return recs.length === 1 ? recs[0]! : null;
  }
  if (slug === "trust-reattach") {
    if (issue.recommendation?.label === TRUST_REATTACH_SPLIT_FIRST_LABEL) {
      return null;
    }
    // Single quarantined-household ref for the non-auto-resolvable
    // statuses (confident_match, when present, carries a SECOND
    // household ref and resolves via resolve-trust-reattach instead —
    // never reaches here as "review").
    const recs = (issue.affectedRecords ?? []).filter(
      (r) => r.kind === "household",
    );
    return recs.length === 1 ? recs[0]! : null;
  }
  return null;
}
